import { Box, Tooltip as MuiTooltip } from "@mui/material";
import { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "../../theme/Theme";
import { useState, useRef, useEffect } from "react";

interface Props extends TooltipProps {
  readonly openOnHover?: boolean;
}

export const Tooltip = styled(({ className, openOnHover, children, ...props }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleMouseEnter = () => {
      setIsOpen(true);
    };

    const handleMouseLeave = () => {
      setIsOpen(false);
    };

    if (openOnHover) {
      tooltipRef.current?.addEventListener("mouseenter", handleMouseEnter);
      tooltipRef.current?.addEventListener("mouseleave", handleMouseLeave);

      return () => {
        tooltipRef.current?.removeEventListener("mouseenter", handleMouseEnter);
        tooltipRef.current?.removeEventListener("mouseleave", handleMouseLeave);
      };
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, openOnHover]);

  const handleClick = () => {
    setIsOpen(true);
  };

  return (
    <div ref={tooltipRef}>
      <MuiTooltip
        arrow={true}
        open={isOpen}
        leaveDelay={3000}
        onClose={() => setIsOpen(false)}
        {...props}
        classes={{ popper: className }}
      >
        <Box sx={{ cursor: "pointer" }} onClick={handleClick}>
          {children}
        </Box>
      </MuiTooltip>
    </div>
  );
})(({ theme }) => {
  const tooltipShadow =
    theme.palette.mode === "dark"
      ? "0px 4px 10px rgba(206, 206, 206, 0.15)"
      : "0px 0px 1px 0px rgba(0, 0, 0, 0.3)";

  const tooltipArrowShadow =
    theme.palette.mode === "dark"
      ? "-0.4px -0.4px 0px 0px  rgba(206, 206, 206, 0.15) !important"
      : "-0.4px -0.4px 0px 0px rgba(0, 0, 0, 0.3) !important";

  return {
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.walter.main,
      color: theme.palette.gary.main,
      boxShadow: tooltipShadow,
      padding: "8px 12px",
      borderRadius: theme.typography.pxToRem(8),
      ...theme.typography.text4,
    },
    [`& .${tooltipClasses.arrow}`]: {
      "&:before": {
        backgroundColor: theme.palette.walter.main,
      },
      content: '""',
      backgroundColor: theme.palette.walter.main,
      position: "absolute",
      borderRadius: "4px",
      width: "15px !important",
      height: "15px !important",
      transform: "rotate(-45deg) skew(10deg, 10deg) !important",
      top: "auto !important",
      bottom: "auto !important",
    },
    [`& .${tooltipClasses.tooltipPlacementLeft}`]: {
      [`& .${tooltipClasses.arrow}`]: {
        right: "2px !important",
      },
    },
    [`& .${tooltipClasses.tooltipPlacementRight}`]: {
      [`& .${tooltipClasses.arrow}`]: {
        left: "2px !important",
        boxShadow: tooltipArrowShadow,
      },
    },
  };
});
